import { css } from '@lastbrand/web-ui-styled-system/css'

import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { CLPLayoutContainerLegacy } from './legacy-clp-layout-container'

import type { ReactNode } from 'react'

export interface CLPLayoutContainerProps {
  /**
   * When the first widget on the page is a banner, suppress the top margin so
   * the banner's own top spacing isn't doubled.
   */
  isBannerFirst: boolean
  children: ReactNode
}

/**
 * DS variant — uses a Panda inline `marginTop: 'space-20'`. Kept inline in this
 * switcher file because the DS body is one `<div>` and does NOT import any
 * SCSS module: the legacy SCSS is confined to `legacy-clp-layout-container.tsx`,
 * so DS users never pull the `.module.scss` into the bundle's reachable code
 * path.
 */
function CLPLayoutContainerDS({ isBannerFirst, children }: CLPLayoutContainerProps) {
  return (
    <div
      className={css({
        // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token (not user-facing copy)
        marginTop: isBannerFirst ? '0' : { base: 'space-20', lg: 'space-24' },
      })}
    >
      {children}
    </div>
  )
}

/**
 * Picks between the SCSS-backed legacy container and the Panda-backed DS
 * container based on the per-request DS rollout decision (`shouldUseDesignSystem()`).
 *
 * Why this exists at the page-renderer layer (not a widget): the wrapping
 * `.container { margin-top: 2.4rem }` lives in the CLP1 page renderer, not in
 * a reusable component, so the gate has to sit where the rule is applied.
 */
export function CLPLayoutContainer(props: CLPLayoutContainerProps) {
  if (shouldUseDesignSystem()) {
    return <CLPLayoutContainerDS {...props} />
  }
  return <CLPLayoutContainerLegacy {...props} />
}
